import { VerticalSpace } from '@brokerbay/design-components';
import { ListingController } from 'api';
import { Button, Col, Icon, Input, Row, Text, message } from 'components';
import React from 'react';
import styled from 'styled-components';

type ListingObj = { id?: string; mlsNumber: string; oid: string };

export type BookingInputsProps = {
  listings: ListingObj[];
  setListings: React.Dispatch<React.SetStateAction<ListingObj[]>>;
  mlsType: string;
  setMlsType: React.Dispatch<React.SetStateAction<string>>;
};

const BookingInput = ({
  listings,
  setListings,
  mlsType,
  setMlsType,
}: BookingInputsProps) => {
  const fetchListing = async (index: number) => {
    try {
      const listing = await ListingController.getListing(listings[index].id!);
      setListings(prev =>
        prev
          .slice(0, index)
          .concat([
            {
              ...prev[index],
              oid: listing.oid || 'N/A',
              mlsNumber: listing.mls_numbers?.[0].number || 'N/A',
            },
          ])
          .concat(prev.slice(index + 1)),
      );
    } catch (err) {
      message.error(
        err?.message ||
          `Could not fetch listing with id: ${listings[index]?.id}`,
      );
    }
  };

  return (
    <VerticalSpace>
      {listings.map(({ id, mlsNumber, oid }, index) => (
          <VerticalSpace key={index}>
            <Text>
              Enter MLS # and OID of Listing {index + 1}:&nbsp;&nbsp;
              {index !== 0 && (
                <Button
                  onClick={() =>
                    setListings(prev =>
                      prev.slice(0, index).concat(prev.slice(index + 1)),
                    )
                  }
                  icon={<Icon type="delete" width={14} height={14} />}
                  data-cy={`testOidc-deleteListing${index}-button`}
                />
              )}
            </Text>
            <Row gutter={16}>
              <Col span={6}>
                <Input
                  key={index}
                  onChange={e => {
                    const { value } = e.target;
                    setListings(prev =>
                      prev
                        .slice(0, index)
                        .concat([{ id, oid, mlsNumber: value }])
                        .concat(prev.slice(index + 1)),
                    );
                  }}
                  value={mlsNumber}
                  placeholder="Mls Number"
                  data-cy={`testOidc-input-listingMlsNumber${index}`}
                />
              </Col>
              <Col span={6}>
                <Input
                  key={index}
                  onChange={e => {
                    const { value } = e.target;
                    setListings(prev =>
                      prev
                        .slice(0, index)
                        .concat([{ id, mlsNumber, oid: value }])
                        .concat(prev.slice(index + 1)),
                    );
                  }}
                  value={oid}
                  placeholder="Oid"
                  data-cy={`testOidc-input-listingOid${index}`}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Input
                  key={index}
                  onChange={e => {
                    const { value } = e.target;
                    setListings(prev =>
                      prev
                        .slice(0, index)
                        .concat([{ mlsNumber, oid, id: value }])
                        .concat(prev.slice(index + 1)),
                    );
                  }}
                  data-cy={`testOidc-input-listingId${index}`}
                  addonAfter={
                    <Button
                      onClick={() => fetchListing(index)}
                      disabled={!id}
                      data-cy={`testOidc-populateListing${index}-button`}
                    >
                      Fetch & Populate
                    </Button>
                  }
                  value={id}
                  placeholder="Listing Id"
                />
              </Col>
            </Row>
          </VerticalSpace>
        ))}
      <Row gutter={16}>
        <Col span={12}>
          <Button
            block
            onClick={() =>
              setListings(prev => [
                ...prev,
                { id: undefined, mlsNumber: '', oid: '' },
              ])
            }
            data-cy="testOidc-addListing-button"
          >
            Add Listing
          </Button>
        </Col>
      </Row>

      <Text>
        Enter <kbd>mlsType</kbd> or MLS ouid:
      </Text>
      <StyledInput
        onChange={e => setMlsType(e.target.value)}
        value={mlsType}
        placeholder="mlsType or ouid"
        dataCy="testOidc-input-mlsType"
      />
    </VerticalSpace>
  );
};

export default BookingInput;

const StyledInput = styled(Input)`
  width: 500px;
`;
